import React, { useEffect, useCallback } from "react";
import Contexts from "../contexts/index";
import { authProvider } from "../services/auth";
import { IUser, initialState } from "../contexts/types";
import { useGetVerifyPlansMutation } from "../hooks/billing";
import SecureStorage from "../utils/secureStorage";
import { PermissionManager } from "../utils/permissions";
import { requiresPaymentVerification } from "../utils/rolePaymentConfig";
import { bootstrapImpersonationFromUrl } from "../services/impersonationClient";

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { mutateAsync: verifyPaymentMutation } = useGetVerifyPlansMutation();

  const [isLoading, setIsLoading] = React.useState(true);
  let [user, setUser] = React.useState<IUser>(initialState);
  let [customer_identification, setCustomer_identification] =
    React.useState<string>("");

  const [upgradeNow, setUpgradeNow] = React.useState(false);
  const [userEndedSubscription, setUserEndedSubscription] =
    React.useState(false);

  const PAYMENT_CHECK_INTERVAL = 3600000;

  const isPaymentStatusStale = useCallback(
    (lastPaymentCheck: number): boolean => {
      if (!lastPaymentCheck) return true;
      const timeDiff = Date.now() - lastPaymentCheck;
      return timeDiff > PAYMENT_CHECK_INTERVAL;
    },
    []
  );

  const updateUserWithFreshPaymentData = useCallback(
    async (userData: any) => {
      if (!userData?.user_identification || !userData?.entity_identification) {
        return userData;
      }
      try {
        const paymentData = await verifyPaymentMutation({
          user_identification: userData.user_identification,
          entity_identification: userData.entity_identification,
        });

        const element = paymentData.data?.element;
        const borrowsOwnerPlan = element?.borrows_owner_plan === true;

        const updatedData = {
          ...userData,
          customer_plans: element?.customer_plans || [],
          customer_top_plan: element?.customer_top_plan || "",
          is_new_user: element?.is_new_user || false,
          trial_left_days: element?.trial_left_days || 0,
          is_promo_trial: element?.is_promo_trial || false,
          trial_restrictions: element?.trial_restrictions,
          trial_end_date: element?.trial_end_date,
          lastPaymentCheck: Date.now(),
          hasValidSubscription: borrowsOwnerPlan
            ? (element?.owner_plans || []).length > 0
            : (element?.customer_plans || []).length > 0,
          borrows_owner_plan: borrowsOwnerPlan,
          owner_plans: element?.owner_plans || [],
          owner_top_plan: element?.owner_top_plan || "",
        };

        if (SecureStorage.isImpersonating()) {
          SecureStorage.updateAuthData(updatedData);
        } else {
          const expiry = userData.rememberMe
            ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            : new Date(Date.now() + 24 * 60 * 60 * 1000);

          SecureStorage.updateAuthData({ ...updatedData, exp: expiry });
        }

        const hasActiveSubscription = borrowsOwnerPlan
          ? (element?.owner_plans || []).length > 0
          : (element?.customer_plans || []).length > 0;
        setUserEndedSubscription(!hasActiveSubscription);

        return updatedData;
      } catch (error) {
        return userData;
      }
    },
    [verifyPaymentMutation]
  );

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      await bootstrapImpersonationFromUrl();

      const sessionData = SecureStorage.getAuthServiceData();

      if (sessionData && !sessionData.exp && SecureStorage.isCustomerTab()) {
        if (isMounted) {
          setIsLoading(false);
        }
        return;
      }

      if (sessionData) {
        try {
          const { exp, rememberMe, lastPaymentCheck, ...rest } = sessionData;

          if (exp && new Date(exp) >= new Date()) {
            let userData: any = { ...rest, rememberMe };

            const userRole = userData.user_role;
            const needsPaymentCheck = requiresPaymentVerification(userRole);
            const isTeamMember = userData.borrows_owner_plan === true;

            if (!needsPaymentCheck && !isTeamMember) {
              setUserEndedSubscription(false);
            } else {
              const isStale = isPaymentStatusStale(lastPaymentCheck);

              if (
                isStale &&
                userData.user_identification &&
                userData.entity_identification
              ) {
                userData = await updateUserWithFreshPaymentData(userData);
              } else {
                const hasValidSubscription = isTeamMember
                  ? (userData.owner_plans || []).length > 0
                  : (userData.customer_plans || []).length > 0;
                setUserEndedSubscription(!hasValidSubscription);
              }
            }

            if (isMounted) {
              authProvider.login(() => {
                setUser({
                  ...initialState,
                  ...userData,
                  isAuthenticated: true,
                  rememberMe: userData.rememberMe || false,
                  user_role: userData.user_role || "DEFAULT",
                  user_type: userData.user_type || "DEFAULT",
                  borrows_owner_plan: isTeamMember,
                  owner_plans: userData.owner_plans || [],
                  owner_top_plan: userData.owner_top_plan || "",
                });
                setCustomer_identification(userData.user_identification || "");
              });
              setIsLoading(false);
            }
          } else {
            if (isMounted) {
              setIsLoading(false);
              SecureStorage.clearAuthData();
              if (SecureStorage.isImpersonationTab()) {
                window.location.href = "/impersonation-ended";
              }
            }
          }
        } catch (error) {
          console.error("Error parsing session data:", error);
          if (isMounted) {
            SecureStorage.clearAuthData();
            setIsLoading(false);
          }
        }
      } else {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [isPaymentStatusStale, updateUserWithFreshPaymentData]);

  let login = (data: any) => {
    const normalizedData = {
      ...data,
      user_role: data.user_role || "DEFAULT",
      user_type: data.user_type || "DEFAULT",
      borrows_owner_plan: data.borrows_owner_plan === true,
      owner_plans: data.owner_plans || [],
      owner_top_plan: data.owner_top_plan || "",
    };

    if (normalizedData.rememberMe) {
      let exp = new Date();
      exp.setDate(exp.getDate() + 30);

      SecureStorage.updateAuthData({ ...normalizedData, exp });
    } else {
      let exp = new Date();
      exp.setDate(exp.getDate() + 1);

      SecureStorage.updateAuthData({ ...normalizedData, exp });
    }

    authProvider.login(() => {
      setUser({ ...initialState, ...normalizedData, isAuthenticated: true });
      setCustomer_identification(normalizedData.user_identification);
    });

    setIsLoading(false);
    return;
  };

  let logout = () => {
    return authProvider.logout(() => {
      setUser(initialState);
    });
  };

  let onOffUpgradeNow = (onOff: boolean) => {
    return authProvider.onOffUpgradeNow(() => {
      setUpgradeNow(onOff);
    });
  };

  let onOffUserEndedSubscription = (onOff: boolean) => {
    return authProvider.onOffUserEndedSubscription(() => {
      setUserEndedSubscription(onOff);
    });
  };

  const permissions = new PermissionManager(user);

  let value = {
    user,
    isLoading,
    customer_identification,
    upgradeNow,
    userEndedSubscription,
    permissions,
    setUser,
    login,
    logout,
    onOffUpgradeNow,
    onOffUserEndedSubscription,
  };

  return (
    <Contexts.AuthContext.Provider value={value}>
      {children}
    </Contexts.AuthContext.Provider>
  );
};

export default AuthProvider;

export {
  PermissionManager,
  OptimizedPermissionManager,
  usePermissions,
  useOptimizedPermissions,
  PlanTier,
  FEATURE_PERMISSIONS,
  PLAN_MAPPINGS,
  USER_LIMITS,
  isFreePlan,
} from "../utils/permissions";
